import type { MeansOfTransportationMetrics } from '../../components/charts/MeansOfTransportation';
import { buildTrendSentence } from './optionalClauseTokens';
import type { NarrativeTokenMap } from './narrativeTemplateEngine';
import { isFiniteNumber } from './narrativeFormatting';

function formatPointChange(change: number): string {
  const abs = Math.abs(change).toFixed(1);
  return change > 0 ? `increased ${abs}` : `decreased ${abs}`;
}

function buildModeShareTrendSentence(m: MeansOfTransportationMetrics): string {
  if (!isFiniteNumber(m.baselineYear) || m.latestYear == null) return '';

  const carChange = m.carPointChange;
  const wfhChange = m.wfhPointChange;
  const carMoved = carChange != null && Math.abs(carChange) >= 0.05;
  const wfhMoved = wfhChange != null && Math.abs(wfhChange) >= 0.05;

  const covidFraming =
    m.baselineYear === 2019
      ? ' Following the COVID-19 pandemic,'
      : ` Since ${m.baselineYear},`;

  if (carMoved && wfhMoved) {
    return (
      `${covidFraming} car, truck, or van share ${formatPointChange(carChange!)} percentage points` +
      ` and working from home ${formatPointChange(wfhChange!)} percentage points` +
      ` (through ${m.latestYear}), reflecting a major shift in how workers commute.`
    );
  }

  if (carMoved) {
    return (
      `${covidFraming} car, truck, or van share ${formatPointChange(carChange!)} percentage points through ${m.latestYear}.`
    );
  }

  if (wfhMoved) {
    return (
      `${covidFraming} working from home ${formatPointChange(wfhChange!)} percentage points through ${m.latestYear}.`
    );
  }

  if (carChange != null) {
    return ` Car, truck, or van share changed little since ${m.baselineYear}.`;
  }

  return '';
}

export function buildCommuteModeShareNarrativeTokens(
  locationName: string,
  m: MeansOfTransportationMetrics | null
): NarrativeTokenMap {
  if (!m) return { locationName, latestYear: null };

  const carShare = m.carLatest;
  const transitShare = m.transitLatest;
  const wfhShare = m.wfhLatest;
  const activeShare =
    m.walkLatest != null && m.bikeLatest != null ? m.walkLatest + m.bikeLatest : m.activeLatest;

  const candidates: Array<{ label: string; share: number | null }> = [
    { label: 'Car, truck, or van', share: carShare },
    { label: 'Worked from home', share: wfhShare },
    { label: 'Public transit', share: transitShare },
    { label: 'Walked or bicycled', share: activeShare },
  ];
  const ranked = candidates.filter((c) => isFiniteNumber(c.share)).sort((a, b) => (b.share! - a.share!));
  const dominant = ranked[0];

  const modeShareTrendSentence = buildModeShareTrendSentence(m);

  return {
    locationName,
    latestYear: m.latestYear,
    dominantMode: dominant?.label ?? 'Car, truck, or van',
    dominantModeShare: dominant?.share != null ? Number(dominant.share.toFixed(1)) : null,
    carShare: carShare != null ? Number(carShare.toFixed(1)) : null,
    transitShare: transitShare != null ? Number(transitShare.toFixed(1)) : null,
    activeShare: activeShare != null ? Number(activeShare.toFixed(1)) : null,
    wfhShare: wfhShare != null ? Number(wfhShare.toFixed(1)) : null,
    modeShareTrendSentence,
    trendSentence: '',
  };
}
